import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { noOp } from '../../lib/utils';

const BASE_RADIO_OUTER_CLASSES =
  'inline-block inline-flex justify-center items-center border mr-1 select-none w-4 h-4 rounded-full flex-shrink-0';
const DEFAULT_RADIO_OUTER_CLASSES = `${BASE_RADIO_OUTER_CLASSES} border-gray-500 bg-white hover:border-blue-100 focus:border-blue-100`;
const DISABLED_RADIO_OUTER_CLASSES = `${BASE_RADIO_OUTER_CLASSES} bg-gray-100 border-gray-200`;
const ERROR_RADIO_OUTER_CLASSES = `${BASE_RADIO_OUTER_CLASSES} bg-white border-error`;
const CHECKED_RADIO_CLASSES = 'w-2 h-2 rounded-full flex-shrink-0 bg-blue-100';

export type RadioProps = {
  /**
   * Checked status of the radio
   */
  initialSelected?: boolean;
  /**
   * Label of the radio
   */
  label?: string;
  /**
   * Value of the radio
   */
  value: string;
  /**
   * Flag to disable radio
   */
  disabled?: boolean;
  /**
   * Flag for error
   */
  error?: boolean;
  /**
   * Select function for radio
   *
   * @return boolean if change is accepted or void
   */
  onSelect?: (isChecked: boolean, value: string) => boolean | void;
};

export const Radio: FC<RadioProps> = ({
  initialSelected,
  label,
  value,
  disabled,
  error,
  onSelect = noOp,
}) => {
  const [isSelected, setIsSelected] = useState<boolean>(false);

  useEffect(() => setIsSelected(initialSelected || false), [initialSelected]);

  const radioOuterClasses = useMemo(() => {
    switch (true) {
      case disabled:
        return DISABLED_RADIO_OUTER_CLASSES;
      case error:
        return ERROR_RADIO_OUTER_CLASSES;
      default:
        return DEFAULT_RADIO_OUTER_CLASSES;
    }
  }, [disabled, error]);

  const changeRadio = useCallback(() => {
    if (!disabled) {
      if (onSelect) {
        onSelect(!isSelected, value);
      }
    }
  }, [disabled, isSelected, onSelect, value]);

  return (
    <div
      className={`flex ${
        disabled
          ? 'cursor-default pointer-events-none text-gray-400'
          : 'cursor-pointer text-gray-700'
      }`}
    >
      <div
        className={radioOuterClasses}
        onClick={changeRadio}
        data-testid="radio-change"
      >
        {isSelected && (
          <div className={CHECKED_RADIO_CLASSES} data-testid="checkbox-inner" />
        )}
      </div>
      <label>
        <input
          type="radio"
          className="hidden"
          name={value}
          id={value}
          checked={isSelected}
          onChange={changeRadio}
        />
        {label}
      </label>
    </div>
  );
};
